import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { Box, Button, Box as FormActions, Box as FormFields } from '@node-space/ui-components'
import {
  contactActionAtom,
  contactAtom,
  contactInstrumentActionAtom,
} from 'pages/Contacts/atoms/contactAtoms'
import { TRANSFER_DESTINATION_RESET_FIELDS } from 'pages/Contacts/constants'
import { useFiatInstrumentForm } from 'pages/Contacts/hooks/useFiatInstrumentForm'
import {
  ContactFiatInstrumentFormData,
  ContactFiatInstrumentFormKeys,
} from 'pages/Contacts/schemas/contactFiatInstrumentSchema'
import { Steps } from 'pages/Contacts/types/fiatInstruments'
import {
  getFieldsToValidate,
  populateInstrumentFormWithBeneficiaryDetails,
} from 'pages/Contacts/utils/fiatInstruments'
import { ContactActionIds } from 'types/atoms/atom.contacts'
import {
  BeneficiaryDetailsCurrency,
  BeneficiaryTransferDestination,
  PaymentMethods,
} from 'types/beneficiaries'
import { ContactCard } from '../ContactCard'
import { ContactsSheets } from '../ContactsSheets'
import { BankAccountBeneficiaryDetails } from './BankAccountBeneficiaryDetails'
import { BankAccountDetails } from './BankAccountDetails'
import { CurrencyDetails } from './CurrencyDetails'
import { FiatInstrumentConfirmation } from './FiatInstrumentConfirmation'
import { IntermediaryBankDetails } from './IntermediaryBankDetails'

export const ContactFiatInstrumentForm = () => {
  const { t } = useTranslation()

  const contact = useAtomValue(contactAtom)
  const [contactInstrumentAction, setContactInstrumentAction] = useAtom(contactInstrumentActionAtom)
  const setContact = useSetAtom(contactAtom)
  const [contactAction, setContactAction] = useAtom(contactActionAtom)

  const [isContactSheetOpen, setIsContactSheetOpen] = useState(false)

  const { meta } = contactAction || {}
  const { contactBeneficiary } = meta || {}

  // Used to also get the latest contact value in the useCallback functions
  const contactRef = useRef(contact)
  contactRef.current = contact

  const {
    errors,
    formFieldLabels,
    formValues,
    form,
    isBusinessBankAccount,
    isInternationalBankAccount,
    step,
    clearErrors,
    register,
    setStep,
    setValue,
    trigger,
    setError,
  } = useFiatInstrumentForm()

  const openContactUpdateSheetIfRequired = useCallback(
    (transferDestination: BeneficiaryTransferDestination) => {
      if (!contactRef.current.entity?.address?.region) {
        setContactAction({
          ...contactAction,
          actionId: ContactActionIds.CONTACT,
          header: t('contacts.updateContact'),
          onClose: () => {
            setIsContactSheetOpen(false)
          },
          meta: {
            updateRequiredForBankAccountDestination: transferDestination,
            keepUpdatedContact: true,
          },
        })
        setIsContactSheetOpen(true)
      }
    },
    [contactAction, t]
  )

  useEffect(() => {
    if (contactBeneficiary) {
      handleTransferDestinationSelect(contactBeneficiary?.transferDestination)
      populateInstrumentFormWithBeneficiaryDetails(contactBeneficiary, setValue)
    }
  }, [contactBeneficiary, setValue])

  const handleTransferDestinationSelect = useCallback(
    (transferDestination: BeneficiaryTransferDestination) => {
      const isTransferDestinationChanged =
        transferDestination !== formValues?.transferDestination && !contactBeneficiary
      if (isTransferDestinationChanged) {
        TRANSFER_DESTINATION_RESET_FIELDS.forEach(field => {
          setValue(field as keyof ContactFiatInstrumentFormData, '')
          clearErrors(field as keyof ContactFiatInstrumentFormData)
        })
      }

      setValue('transferDestination', transferDestination)
      const isInternational = transferDestination === BeneficiaryTransferDestination.INTERNATIONAL
      if (isInternational) {
        if (!contactRef.current.entity?.address?.region) {
          openContactUpdateSheetIfRequired(transferDestination)
          return
        }

        setStep(Steps.BANK_DETAILS)
      } else {
        setStep(Steps.BANK_DETAILS)
      }
    },
    [formValues?.transferDestination]
  )

  const fieldsToValidate: ContactFiatInstrumentFormKeys[] = useMemo(() => {
    return getFieldsToValidate(step, formValues)
  }, [step])

  const onContinue = useCallback(async () => {
    const currentFormValues = form?.getValues()

    const { currency, isIntermediaryBankDetailsRequired, paymentMethods } = currentFormValues

    const currentContact = contactRef.current
    const contactHasRegion = !!currentContact?.entity?.address?.region
    const isInternational = paymentMethods?.includes(PaymentMethods.SWIFT)
    const isLocalUsd =
      paymentMethods?.includes(PaymentMethods.SWIFT) &&
      currency === BeneficiaryDetailsCurrency.USD &&
      !isInternational

    if ((isInternational || isLocalUsd) && !contactHasRegion) {
      openContactUpdateSheetIfRequired(BeneficiaryTransferDestination.INTERNATIONAL)
      return
    }

    const isValid = await trigger(fieldsToValidate)

    if (!isValid) {
      return
    }

    switch (step) {
      case Steps.CURRENCY_DETAILS:
        setStep(Steps.BENEFICIARY_DETAILS)
        break
      case Steps.BENEFICIARY_DETAILS:
        setStep(Steps.BANK_DETAILS)
        break
      case Steps.BANK_DETAILS:
        if (isInternational) {
          if (isIntermediaryBankDetailsRequired) {
            setStep(Steps.INTERMEDIARY_BANK_DETAILS)
            return
          } else {
            setStep(Steps.CONFIRMATION)
            return
          }
        } else {
          setStep(Steps.CONFIRMATION)
          return
        }
      case Steps.INTERMEDIARY_BANK_DETAILS:
        setStep(Steps.CONFIRMATION)
        break
    }
  }, [
    contactAction,
    contactInstrumentAction,
    fieldsToValidate,
    form,
    formValues?.isIntermediaryBankDetailsRequired,
    step,
    setContact,
    setContactAction,
    setStep,
    t,
    trigger,
    openContactUpdateSheetIfRequired,
  ])

  const actions = useMemo(() => {
    switch (step) {
      case Steps.CURRENCY_DETAILS:
      case Steps.BENEFICIARY_DETAILS:
      case Steps.BANK_DETAILS:
        return (
          <FormActions flex justify="between" paddingT={20}>
            <Button
              testId="cancel"
              type="button"
              variant="secondary"
              onClick={() => {
                contactInstrumentAction?.onClose()
              }}
            >
              {t('cancel')}
            </Button>
            <Button testId="continue" type="button" variant="primary" onClick={onContinue}>
              {t('continue')}
            </Button>
          </FormActions>
        )
      case Steps.INTERMEDIARY_BANK_DETAILS:
        return (
          <FormActions flex justify="between" paddingT={20}>
            <Button testId="skip-this-step" type="button" variant="secondary" onClick={onContinue}>
              {t('skipThisStep')}
            </Button>
            <Button type="button" testId="continue" variant="primary" onClick={onContinue}>
              {t('continue')}
            </Button>
          </FormActions>
        )
      case Steps.CONFIRMATION:
      case Steps.SUCCESS:
      default:
        return
    }
  }, [step])

  const handleGoBack = () => {
    // Cannot go back from the first step
    if (step === Steps.CURRENCY_DETAILS) {
      return
    }

    const internationalFlow = {
      [Steps.BENEFICIARY_DETAILS]: Steps.CURRENCY_DETAILS,
      [Steps.BANK_DETAILS]: Steps.BENEFICIARY_DETAILS,
      [Steps.INTERMEDIARY_BANK_DETAILS]: Steps.BANK_DETAILS,
      [Steps.CONFIRMATION]: formValues?.isIntermediaryBankDetailsRequired
        ? Steps.INTERMEDIARY_BANK_DETAILS
        : Steps.BANK_DETAILS,
    }

    const localFlow = {
      [Steps.BENEFICIARY_DETAILS]: Steps.CURRENCY_DETAILS,
      [Steps.BANK_DETAILS]: Steps.BENEFICIARY_DETAILS,
      [Steps.CONFIRMATION]: Steps.BANK_DETAILS,
    }

    const navigationFlow = isInternationalBankAccount ? internationalFlow : localFlow
    const previousStep = navigationFlow[step]

    if (previousStep) {
      setStep(previousStep)
    }
  }

  const canGoBack = useMemo(() => {
    return (
      step === Steps.CONFIRMATION ||
      step === Steps.INTERMEDIARY_BANK_DETAILS ||
      step === Steps.BANK_DETAILS ||
      step === Steps.BENEFICIARY_DETAILS
    )
  }, [step])

  const getHeader = useCallback(() => {
    switch (step) {
      case Steps.CURRENCY_DETAILS:
        return t('contacts.addBankAccount')
      case Steps.BENEFICIARY_DETAILS:
        return t('contacts.beneficiaryDetails')
      case Steps.BANK_DETAILS:
        return t('contacts.bankAccountDetails')
      case Steps.INTERMEDIARY_BANK_DETAILS:
        return t('contacts.intermediaryBankDetails')
      case Steps.CONFIRMATION:
        return t('contacts.confirmBankAccountDetails')
      default:
        return t('contacts.addBankAccount')
    }
  }, [step, t])

  useEffect(() => {
    setContactInstrumentAction({
      ...contactInstrumentAction,
      header: getHeader(),
      onBack: canGoBack ? handleGoBack : null,
    })
  }, [step, canGoBack])

  return (
    <Box width="full">
      <form data-testid="contact-bank-account-form">
        <FormFields width="full" flexCol gapY={16} paddingT={24}>
          {/* Step 1: Basic account details */}
          {step === Steps.CURRENCY_DETAILS && (
            <Box paddingB={8}>
              <ContactCard name={contact?.name} title={t('contacts.contact')} showAvatar={false} />
              <CurrencyDetails
                formValues={formValues}
                errors={errors}
                isInternationalBankAccount={isInternationalBankAccount}
                clearErrors={clearErrors}
                setValue={setValue}
                register={register}
                setIsContactSheetOpen={setIsContactSheetOpen}
              />
            </Box>
          )}
          {/* Step 2: Beneficiary details */}
          {step === Steps.BENEFICIARY_DETAILS && (
            <Box paddingB={8}>
              <ContactCard name={contact?.name} title={t('contacts.contact')} showAvatar={false} />
              <BankAccountBeneficiaryDetails
                formFieldLabels={formFieldLabels}
                formValues={formValues}
                errors={errors}
                isBusinessAccount={isBusinessBankAccount}
                isInternationalBankAccount={isInternationalBankAccount}
                clearErrors={clearErrors}
                setValue={setValue}
                register={register}
                setError={setError}
                setIsContactSheetOpen={setIsContactSheetOpen}
                contactBeneficiary={contactBeneficiary}
              />
            </Box>
          )}

          {/* Step 3: Bank account details */}
          {step === Steps.BANK_DETAILS && (
            <Box paddingB={8}>
              <ContactCard name={contact?.name} title={t('contacts.contact')} showAvatar={false} />
              <BankAccountDetails
                formFieldLabels={formFieldLabels}
                formValues={formValues}
                isBusinessAccount={isBusinessBankAccount}
                isInternationalBankAccount={isInternationalBankAccount}
                setValue={setValue}
                clearErrors={clearErrors}
                trigger={trigger}
                setError={setError}
                register={register}
                errors={errors}
                contactBeneficiary={contactBeneficiary}
              />
            </Box>
          )}

          {/* Step 4: Intermediary bank details */}
          {step === Steps.INTERMEDIARY_BANK_DETAILS && (
            <Box>
              <ContactCard name={contact?.name} title={t('contacts.contact')} showAvatar={false} />
              <IntermediaryBankDetails
                formValues={formValues}
                errors={errors}
                clearErrors={clearErrors}
                register={register}
                setError={setError}
                setValue={setValue}
                trigger={trigger}
                contactBeneficiary={contactBeneficiary}
              />
            </Box>
          )}

          {/* Step 5: Confirmation */}
          {step === Steps.CONFIRMATION && (
            <Box paddingB={12}>
              <ContactCard name={contact?.name} title={t('contacts.contact')} showAvatar={false} />
              <FiatInstrumentConfirmation
                formValues={formValues}
                formFieldLabels={formFieldLabels}
                form={form}
                isLoading={false}
              />
            </Box>
          )}
        </FormFields>

        {actions}
      </form>
      <ContactsSheets isSheetOpen={isContactSheetOpen} isNestedSheet />
    </Box>
  )
}
